import React, { useState, useRef, useEffect } from 'react';
import { MoreVertical } from 'lucide-react';

/**
 * Compact kebab menu component for table actions
 */
const KebabMenu = ({
  children,
  className = '',
  buttonClassName = '',
  menuClassName = '',
  disabled = false,
  align = 'right' // 'left' or 'right'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropUp, setDropUp] = useState(false);
  const menuRef = useRef(null);
  const buttonRef = useRef(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Close menu on escape key
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen]);

  const toggleMenu = (e) => {
    e.stopPropagation();
    if (!disabled) {
      if (!isOpen && buttonRef.current) {
        // Check if there's enough space below the button
        const buttonRect = buttonRef.current.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const spaceBelow = viewportHeight - buttonRect.bottom;
        const menuHeight = 120; // Approximate menu height

        setDropUp(spaceBelow < menuHeight && buttonRect.top > menuHeight);
      }
      setIsOpen(!isOpen);
    }
  };

  const closeMenu = () => {
    setIsOpen(false);
  };

  const alignmentClasses = align === 'left'
    ? 'left-0'
    : 'right-0';

  const positionClasses = dropUp
    ? `bottom-full mb-1 ${alignmentClasses} origin-bottom-${align}`
    : `top-full mt-1 ${alignmentClasses} origin-top-${align}`;

  return (
    <div className={`relative inline-block ${className}`} ref={menuRef}>
      <button
        ref={buttonRef}
        onClick={toggleMenu}
        disabled={disabled}
        className={`
          inline-flex items-center justify-center w-6 h-6 text-slate-400 hover:text-slate-600
          hover:bg-slate-100 rounded transition-colors duration-150 focus:outline-none
          focus:ring-2 focus:ring-blue-500 focus:ring-offset-1
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
          ${buttonClassName}
        `}
        aria-label="More actions"
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <MoreVertical className="w-4 h-4" />
      </button>

      {isOpen && (
        <div
          className={`
            absolute z-[100] min-w-[120px] bg-white rounded-md shadow-lg border border-slate-200
            ${positionClasses} ${menuClassName}
          `}
          role="menu"
          aria-orientation="vertical"
        >
          <div className="py-1">
            {React.Children.map(children, (child, index) =>
              React.cloneElement(child, {
                key: index,
                onClick: (e) => {
                  if (child.props.onClick) {
                    child.props.onClick(e);
                  }
                  closeMenu();
                }
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Menu item component for use within KebabMenu
 */
export const KebabMenuItem = ({
  children,
  onClick,
  icon: Icon,
  className = '',
  variant = 'default', // 'default', 'danger'
  disabled = false
}) => {
  const variantClasses = {
    default: 'text-slate-700 hover:bg-slate-50 hover:text-slate-900',
    danger: 'text-red-600 hover:bg-red-50 hover:text-red-700'
  };

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`
        w-full text-left px-3 py-1.5 text-xs font-medium flex items-center space-x-2
        transition-colors duration-150 focus:outline-none focus:bg-slate-50
        ${variantClasses[variant]}
        ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
        ${className}
      `}
      role="menuitem"
    >
      {Icon && <Icon className="w-3 h-3 flex-shrink-0" />}
      <span className="truncate">{children}</span>
    </button>
  );
};

/**
 * Menu divider component
 */
export const KebabMenuDivider = () => (
  <div className="border-t border-slate-200 my-1" role="separator" />
);

export default KebabMenu;
